//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Control for handling a selection of objects
//
//  Copyright:    © 2021 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier            Issue# Description
//region Version 1.0.0.0
//    001            Sean Flook                 Initial Revision.
//    002   18.04.23 Sean Flook         WI40685 Do not allow properties to be created on a closed street, a type 3/4 street or a rejected/historic parent property.
//    003   28.06.23 Sean Flook         WI40256 Changed Extent to Provenance where appropriate.
//    004   24.07.23 Sean Flook                 Removed Edit street and Edit property menu items.
//    005   07.09.23 Sean Flook                 Added code to handle selected ESUs.
//    006   20.09.23 Sean Flook                 Added ability to show streets and properties in Google street view. Also added successor, organisation and classification record types.
//    007   06.10.23 Sean Flook                 Added some error trapping and use colour variables.
//    008   27.10.23 Sean Flook       IMANN-175 Changes required for multi-edit of properties.
//    009   03.11.23 Sean Flook       IMANN-175 Use the same menu item for classification.
//    010   10.11.23 Sean Flook       IMANN-175 Changes required for Move BLPU seed point.
//    011   24.11.23 Sean Flook                 Moved Box and Stack to @mui/system, use getClassificationCode method and renamed successor to successorCrossRef.
//    012   05.01.24 Sean Flook                 Use CSS shortcuts.
//    013   10.01.24 Sean Flook                 Hide Create street from selected ESUs button until code has been written (IMANN-216).
//    014   16.01.24 Sean Flook                 Changes required to fix warnings.
//    015   25.01.24 Sean Flook                 Changes required after UX review.
//    016   25.01.24 Sean Flook                 Make the create street button visible.
//    017   31.01.24 Joel Benford               Show addList only in dev
//    018   09.02.24 Sean Flook                 Added extents.
//    019   09.02.24 Sean Flook                 Removed Create street button for ESUs.
//    020   20.02.24 Sean Flook            MUL1 Changes required to handle selecting properties from the map.
//    021   27.02.24 Sean Flook           MUL16 Changes required to handle parent child relationships.
//    022   12.03.24 Sean Flook            MUL8 Added onPropertyMoved.
//    023   13.03.24 Sean Flook            MUL9 No need to do resets here.
//    024   25.03.24 Sean Flook           MUL16 Removed option to remove from parent.
//    025   04.04.24 Sean Flook                 Added parentUprn to mapContext search data for properties.
//    026   08.05.24 Sean Flook       IMANN-447 Added exclude from export and site visit to the options.
//    027   19.06.24 Sean Flook       IMANN-629 Changes to code so that current user is remembered and a 401 error displays the login dialog.
//    028   20.06.24 Sean Flook       IMANN-636 Use the new user rights.
//    029   02.07.24 Sean Flook       IMANN-582 Added ability to edit state for multiple properties.
//    030   05.07.24 Sean Flook       IMANN-692 Added new parameter in call to UpdateRangeAfterSave.
//    031   09.07.24 Sean Flook       IMANN-582 Changed case.
//    032   11.07.24 Sean Flook       IMANN-747 Only display menu items if the user has the rights to use them.
//    033   11.07.24 Sean Flook       IMANN-748 Only display menu items if user has the correct rights.
//    034   17.07.24 Joshua McCormick IMANN-548 zoomToStreet fix
//    035   17.07.24 Joshua McCormick IMANN-548 changed FormatStreetData to getStreetSearchData, Removed find debug code in zoomToStreet
//    036   18.07.24 Sean Flook       IMANN-761 Remove the Close and Delete menu options for streets.
//    036   18.07.24 Sean Flook       IMANN-773 Correctly set the parent information when creating property/ies.
//    037   28.08.24 Sean Flook       IMANN-957 Added missing formattedAddress field to map search data.
//    038   10.09.24 Sean Flook       IMANN-980 Only write to the console if the user has the showMessages right.
//endregion Version 1.0.0.0
//region Version 1.0.1.0
//    039   27.09.24 Sean Flook       IMANN-573 when creating a new child or range of children check the parent is not already at the maximum allowable level.
//    040   14.10.24 Sean Flook      IMANN-1016 Changes required to handle LLPG Streets.
//    041   31.10.24 Sean Flook      IMANN-1012 Added the plot to postal wizard.
//endregion Version 1.0.1.0
//region Version 1.0.2.0
//    042   13.11.24 Sean Flook      IMANN-1012 Made the plot to postal wizard menu item visible.
//    043   18.11.24 Sean Flook      IMANN-1056 Use the new getPropertyListDetails method.
//endregion Version 1.0.2.0
//region Version 1.0.4.0
//    044   11.02.25 Sean Flook       IMANN-1686 Correctly get the map search data when removing from list.
//endregion Version 1.0.4.0
//region Version 1.0.4.0
//    045   18.03.25 Sean Flook        IMANN-885 If street closed do not allow properties to be created on it.
//    046   18.03.25 Sean Flook        IMANN-885 Cannot rely on the search results, so use isStreetClosed to determine if the street is closed or not.
//endregion Version 1.0.4.0
//region Version 1.0.5.0
//    047   22.04.25 Sean Flook       IMANN-1721 Correctly determine if the current street is closed or not.
//    048   23.04.25 Sean Flook       IMANN-1768 Changes required for selecting properties in property and street forms.
//    049   24.04.25 Sean Flook       IMANN-1760 Prevent multiple renders from occurring.
//    050   29.05.25 Sean Flook       IMANN-1802 Use the new GetSearchStreetMapData method rather than looping through the streets individually.
//endregion Version 1.0.5.0
//region Version 1.0.6.0
//    051   20.06.25 Sean Flook       IMANN-1813 Added the ability to export street and property records to Excel.
//    052   04.07.25 Sean Flook                  Changed to use variant rather than lots of boolean parameters.
//    053   17.07.25 Sean Flook       IMANN-1864 Updated call to UpdateRangeAfterSave.
//    054   12.08.25 Sean Flook       IMANN-1986 Include variant street&Property in handleExportTo method.
//endregion Version 1.0.6.0
//region Version 1.0.6.4
//    055   30.09.25 Sean Flook       IMANN-2074 Clear the wizard coordinates when the dialog is closed.
//endregion Version 1.0.6.4
//region Version 1.0.7.0
//    056   13.06.25 Sean Flook       IMANN-1812 Modified to use the new Sandbox store.
//    057   22.08.25 Sean Flook       IMANN-1812 Fixed merge issues.
//    058   28.08.25 Sean Flook       IMANN-1812 Removed NestedMenuItem as not compatible with newer version of MUI.
//    059   23.09.25 Sean Flook       IMANN-2041 Set the historical field for extents.
//    060   27.10.25 Sean Flook       IMANN-2092 Use the new spatialReferenceWkid property from mapContext to set the spatialReference used by the layers in the map.
//    061   03.11.25 Sean Flook       IMANN-2073 Allow zooming to a group of properties and streets as well as creating a new list.
//    062   04.11.25 Sean Flook       IMANN-2120 Reinstated NestedMenuItem.
//    063   06.11.25 Sean Flook       IMANN-2073 Added Create new list and Remove from list options when we have streets and properties selected and tidied the code a bit.
//    064   07.11.25 Sean Flook       IMANN-2073 Removed menu items as duplicate of buttons which are already there.
//    065   17.12.25 Sean Flook       IMANN-2196 Various changes to allow for the control to work within the maps.
//    066   21.01.26 Sean Flook       IMANN-2204 Replaced calls to GetWktCoordinates with wktToGeometry.
//    067   20.02.26 Sean Flook       IMANN-2329 Updated call to resetPropertyErrors.
//    068   26.02.26 Sean Flook       IMANN-2324 Modified call to GetSearchStreetMapData.
//    069   06.03.26 Sean Flook       IMANN-2109 Renamed the map context property currentSearchData to currentMapSearchData to better reflect its purpose.
//endregion Version 1.0.7.0
//region Version 1.0.7.1
//    070   27.04.26 Sean Flook       IMANN-2411 Correctly compare UPRNs.
//endregion Version 1.0.7.1
//region Version 1.0.7.2
//    071   27.04.26 Sean Flook       IMANN-2441 Call onCloseSelection when closing the move BLPU dialog.
//    072   11.05.26 Sean Flook       IMANN-2442 Use onClearWizardData to clear the wizard data after it has been used.
//    073   02.06.26 Sean Flook       IMANN-2460 Undo changes made for IMANN-2441.
//endregion Version 1.0.7.2
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { useContext, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

import useSandboxStore from "./../context/SandboxStore";

import MapContext from "../context/mapContext";
import UserContext from "../context/userContext";
import StreetContext from "../context/streetContext";
import PropertyContext from "../context/propertyContext";
import SettingsContext from "../context/settingsContext";
import SearchContext from "../context/searchContext";
import InformationContext from "../context/informationContext";
import SelectionContext from "../context/selectionContext";

// import { useSaveConfirmation } from "../pages/SaveConfirmationPage";

import { IconButton, Typography, Menu, MenuItem, Box, Stack } from "@mui/material";
import { NestedMenuItem } from "mui-nested-menu";
import ADSSelectionButton from "./ADSSelectionButton";
import AddPropertyWizardDialog from "../dialogs/AddPropertyWizardDialog";
import MultiEditLogicalStatusDialog from "../dialogs/MultiEditLogicalStatusDialog";
import MultiEditSingleFieldDialog from "../dialogs/MultiEditSingleFieldDialog";
import MultiEditAddressFieldsDialog from "../dialogs/MultiEditAddressFieldsDialog";
import MultiEditAddCrossReferenceDialog from "../dialogs/MultiEditAddCrossReferenceDialog";
import MultiEditRemoveCrossReferenceDialog from "../dialogs/MultiEditRemoveCrossReferenceDialog";
import MultiEditAddClassificationDialog from "../dialogs/MultiEditAddClassificationDialog";
import MoveBLPUDialog from "../dialogs/MoveBLPUDialog";
import MultiEditPlotToPostalWizardDialog from "../dialogs/MultiEditPlotToPostalWizardDialog";

import { copyTextToClipboard, wktToGeometry, openInStreetView } from "../utils/HelperUtils";
import {
  GetExportStreetData,
  GetSearchStreetMapData,
  getStreetListDetails,
  GetStreetMapData,
  getStreetSearchData,
  IsStreetClosed,
} from "../utils/StreetUtils";
import {
  GetParentHierarchy,
  GetPropertyMapData,
  UpdateRangeAfterSave,
  getClassificationCode,
  getPropertyListDetails,
  GetExportPropertyData,
} from "../utils/PropertyUtils";
import { utils, writeFile } from "xlsx";

import Polyline from "@arcgis/core/geometry/Polyline";
import * as disjointOperator from "@arcgis/core/geometry/operators/disjointOperator.js";
import * as unionOperator from "@arcgis/core/geometry/operators/unionOperator.js";

import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

import { adsBlueA, adsWhite, adsLightGreyA50 } from "../utils/ADSColours";
import { menuStyle, menuItemStyle } from "../utils/ADSStyles";
import MakeChildDialog from "../dialogs/MakeChildDialog";

ADSSelectionControl.propTypes = {
  variant: PropTypes.oneOf([
    "",
    "street",
    "property",
    "street&Property",
    "esu",
    "mapEsu",
    "asd",
    "classification",
    "organisation",
    "successorCrossRef",
    "provenance",
    "mapExtent",
    "crossReference",
    "moveBlpu",
    "wizard",
    "mapBlpu",
    "mapProperty",
  ]),
  selectionCount: PropTypes.number.isRequired,
  currentStreet: PropTypes.object,
  currentEsu: PropTypes.array,
  currentProperty: PropTypes.object,
  currentAddress: PropTypes.string,
  currentClassification: PropTypes.array,
  currentOrganisation: PropTypes.array,
  currentSuccessorCrossRef: PropTypes.array,
  currentProvenance: PropTypes.array,
  currentExtent: PropTypes.array,
  currentCrossReference: PropTypes.array,
  streetUsrns: PropTypes.array,
  propertyUprns: PropTypes.array,
  onSetCopyOpen: PropTypes.func,
  onAddNote: PropTypes.func,
  onEditWizard: PropTypes.func,
  onError: PropTypes.func,
  onDeleteClassification: PropTypes.func,
  onDeleteOrganisation: PropTypes.func,
  onDeleteSuccessorCrossRef: PropTypes.func,
  onDeleteProvenance: PropTypes.func,
  onMergeExtent: PropTypes.func,
  onDeleteCrossReference: PropTypes.func,
  onDeleteWizard: PropTypes.func,
  onPropertyMoved: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

function ADSSelectionControl({
  variant = "",
  selectionCount,
  currentStreet,
  currentEsu,
  currentProperty,
  currentAddress,
  currentClassification,
  currentOrganisation,
  currentSuccessorCrossRef,
  currentProvenance,
  currentExtent,
  currentCrossReference,
  streetUsrns,
  propertyUprns,
  onSetCopyOpen,
  onAddNote,
  onEditWizard,
  onError,
  onDeleteClassification,
  onDeleteOrganisation,
  onDeleteSuccessorCrossRef,
  onDeleteProvenance,
  onMergeExtent,
  onDeleteCrossReference,
  onDeleteWizard,
  onPropertyMoved,
  onClose,
}) {
  const sourceSandboxStreet = useSandboxStore((state) => state.sourceStreet);
  const currentSandboxStreet = useSandboxStore((state) => state.currentStreet);
  const setRefreshRelated = useSandboxStore((state) => state.setRefreshRelated);

  const mapContext = useContext(MapContext);
  const userContext = useContext(UserContext);
  const streetContext = useContext(StreetContext);
  const propertyContext = useContext(PropertyContext);
  const settingsContext = useContext(SettingsContext);
  const searchContext = useContext(SearchContext);
  const informationContext = useContext(InformationContext);
  const selectionContext = useContext(SelectionContext);

  const [anchorStreetPropertyActionsEl, setAnchorStreetPropertyActionsEl] = useState(null);
  const [anchorStreetActionsEl, setAnchorStreetActionsEl] = useState(null);
  const [anchorPropertyActionsEl, setAnchorPropertyActionsEl] = useState(null);
  const [anchorPropertySelectionActionsEl, setAnchorPropertySelectionActionsEl] = useState(null);
  const [anchorWizardActionsEl, setAnchorWizardActionsEl] = useState(null);

  const [informationAnchorEl, setInformationAnchorEl] = useState(null);
  const informationOpen = Boolean(informationAnchorEl);

  const propertyActionsOpen = Boolean(anchorPropertyActionsEl);

  const [numberOfTypes, setNumberOfTypes] = useState(0);

  const [userCanEditStreet, setUserCanEditStreet] = useState(false);
  const [userCanEditASD, setUserCanEditASD] = useState(false);
  const [userCanEditProperty, setUserCanEditProperty] = useState(false);

  const [currentUsrn, setCurrentUsrn] = useState(null);
  const [currentUprn, setCurrentUprn] = useState(null);

  const [currentStreetClosed, setCurrentStreetClosed] = useState(false);

  const spatialReferenceWkid = useRef(mapContext.spatialReference || 27700);
  const propertyWizardType = useRef(null);
  const propertyWizardParent = useRef(null);
  const [openPropertyWizard, setOpenPropertyWizard] = useState(false);

  const [editLogicalStatusVariant, setEditLogicalStatusVariant] = useState("unknown");
  const [editSingleFieldVariant, setEditSingleFieldVariant] = useState("unknown");

  const [openEditLogicalStatus, setOpenEditLogicalStatus] = useState(false);
  const [openEditSingleField, setOpenEditSingleField] = useState(false);
  const [openEditAddressFields, setOpenEditAddressFields] = useState(false);
  const [openPlotToPostalWizard, setOpenPlotToPostalWizard] = useState(false);
  const [openAddCrossReference, setOpenAddCrossReference] = useState(false);
  const [openRemoveCrossReference, setOpenRemoveCrossReference] = useState(false);
  const [openAddClassification, setOpenAddClassification] = useState(false);
  const [openMoveBlpu, setOpenMoveBlpu] = useState(false);
  const [openMakeChild, setOpenMakeChild] = useState(false);

  const [makeChildUprn, setMakeChildUprn] = useState([]);
  const [multiEditUprns, setMultiEditUprns] = useState([]);

  /**
   * Event to handle the closing.
   */
  const handleCloseClick = () => {
    if (onClose) onClose();
  };

  /**
   * Method to add a property.
   */
  async function HandleAddProperty() {
    if (currentUsrn) {
      await GetStreetMapData(Number(currentUsrn), userContext, settingsContext.isScottish).then((result) => {
        if (result && result.state !== 4) {
          propertyContext.resetPropertyErrors(false, false);
          propertyContext.onClearWizardData();
          mapContext.onWizardSetCoordinate(null);
          propertyWizardType.current = "property";
          const engDescriptor = result.streetDescriptors.filter((x) => x.language === "ENG");
          propertyWizardParent.current = { usrn: currentUsrn, address: engDescriptor[0].streetDescriptor };
          setOpenPropertyWizard(true);
        } else {
          if (onError) onError("invalidSingleState");
        }
      });
    }
  }

  /**
   * Method to add a range of properties.
   */
  async function HandleAddRange() {
    if (currentUsrn) {
      await GetStreetMapData(Number(currentUsrn), userContext, settingsContext.isScottish).then((result) => {
        if (result && result.state !== 4) {
          propertyContext.resetPropertyErrors(true, true);
          propertyContext.onClearWizardData();
          mapContext.onWizardSetCoordinate(null);
          propertyWizardType.current = "range";
          const engDescriptor = result.streetDescriptors.filter((x) => x.language === "ENG");
          propertyWizardParent.current = { usrn: currentUsrn, address: engDescriptor[0].streetDescriptor };
          setOpenPropertyWizard(true);
        } else {
          if (onError) onError("invalidRangeState");
        }
      });
    }
  }

  /**
   * Event to handle when the property wizard is done.
   */
  const handlePropertyWizardDone = () => {
    setOpenPropertyWizard(false);
  };

  /**
   * Event to handle the closing of the property wizard.
   */
  const handlePropertyWizardClose = () => {
    setOpenPropertyWizard(false);
  };

  /**
   * Event to handle when the edit logical status is closed.
   *
   * @param {array} updatedData Array of properties that have been updated
   */
  const handleEditLogicalStatusClose = (updatedData) => {
    setOpenEditLogicalStatus(false);
    setMultiEditUprns([]);

    if (updatedData && updatedData.length > 0) {
      UpdateRangeAfterSave(
        updatedData,
        userContext,
        mapContext,
        propertyContext,
        settingsContext.isWelsh,
        searchContext,
        true
      );
      setRefreshRelated(true);
    }
  };

  /**
   * Event to handle when the edit single field is closed.
   *
   * @param {array} updatedData Array of properties that have been updated
   */
  const handleEditSingleFieldClose = (updatedData) => {
    setOpenEditSingleField(false);
    setMultiEditUprns([]);

    if (updatedData && updatedData.length > 0) {
      UpdateRangeAfterSave(
        updatedData,
        userContext,
        mapContext,
        propertyContext,
        settingsContext.isWelsh,
        searchContext,
        true
      );
      setRefreshRelated(true);
    }
  };

  /**
   * Event to handle when the edit address fields is closed.
   *
   * @param {array} updatedData Array of properties that have been updated
   */
  const handleEditAddressFieldsClose = (updatedData) => {
    setOpenEditAddressFields(false);
    setMultiEditUprns([]);

    if (updatedData && updatedData.length > 0) {
      UpdateRangeAfterSave(
        updatedData,
        userContext,
        mapContext,
        propertyContext,
        settingsContext.isWelsh,
        searchContext,
        true
      );
      setRefreshRelated(true);
    }
  };

  /**
   * Event to handle when the plot to postal wizard is closed.
   *
   * @param {array} updatedData Array of properties that have been updated
   */
  const handlePlotToPostalWizardClose = (updatedData) => {
    setOpenPlotToPostalWizard(false);
    setMultiEditUprns([]);

    if (updatedData && updatedData.length > 0) {
      UpdateRangeAfterSave(
        updatedData,
        userContext,
        mapContext,
        propertyContext,
        settingsContext.isWelsh,
        searchContext,
        true
      );
      setRefreshRelated(true);
    }
  };

  /**
   * Event to handle when the add cross reference is closed.
   *
   * @param {array} updatedData Array of properties that have been updated
   */
  const handleAddCrossReferenceClose = (updatedData) => {
    setOpenAddCrossReference(false);
    setMultiEditUprns([]);

    if (updatedData && updatedData.length > 0) {
      UpdateRangeAfterSave(
        updatedData,
        userContext,
        mapContext,
        propertyContext,
        settingsContext.isWelsh,
        searchContext,
        true
      );
      setRefreshRelated(true);
    }
  };

  /**
   * Event to handle when the remove cross reference is closed.
   *
   * @param {array} updatedData Array of properties that have been updated
   */
  const handleRemoveCrossReferenceClose = (updatedData) => {
    setOpenRemoveCrossReference(false);
    setMultiEditUprns([]);

    if (updatedData && updatedData.length > 0) {
      UpdateRangeAfterSave(
        updatedData,
        userContext,
        mapContext,
        propertyContext,
        settingsContext.isWelsh,
        searchContext,
        true
      );
      setRefreshRelated(true);
    }
  };

  /**
   * Event to handle when the add classification is closed.
   *
   * @param {array} updatedData Array of properties that have been updated
   */
  const handleAddClassificationClose = (updatedData) => {
    setOpenAddClassification(false);
    setMultiEditUprns([]);

    if (updatedData && updatedData.length > 0) {
      UpdateRangeAfterSave(
        updatedData,
        userContext,
        mapContext,
        propertyContext,
        settingsContext.isWelsh,
        searchContext,
        true
      );
      setRefreshRelated(true);
    }
  };

  /**
   * event to handle when the move BLPU seed point dialog is closed.
   *
   * @param {boolean} saved True if the changes have been saved; otherwise false
   */
  const handleMoveBlpuClose = (saved) => {
    setOpenMoveBlpu(false);
    mapContext.onDisplayWizardMap(false);
    mapContext.onWizardSetCoordinate(null);
    setMultiEditUprns([]);
    if (saved && onPropertyMoved) onPropertyMoved();
  };

  /**
   * event to handle when the make child of dialog is closed.
   */
  const handleMakeChildClose = () => {
    setOpenMakeChild(false);
    searchContext.onHideSearch(false);
  };

  /**
   * Method to update the search and map contexts after creating a new list or removing from list.
   *
   * @param {array} newStreetSearchData The new street search data
   * @param {array} newPropertySearchData The new property search data
   */
  const updateSearchData = async (newStreetSearchData, newPropertySearchData) => {
    const newSearchData = newStreetSearchData.concat(newPropertySearchData);

    const searchStreets = userContext.currentUser.hasStreet
      ? await GetSearchStreetMapData(
          newStreetSearchData,
          userContext.currentUser,
          userContext.onExpired,
          settingsContext.isScottish
        )
      : [];

    const searchLlpgStreets = !userContext.currentUser.hasStreet
      ? await GetSearchStreetMapData(
          newStreetSearchData,
          userContext.currentUser,
          userContext.onExpired,
          settingsContext.isScottish
        )
      : [];

    const searchProperties = newPropertySearchData.map((x) => {
      let propObj = {
        uprn: x.uprn,
        parentUprn: x.parent_uprn,
        address: x.address,
        formattedAddress: x.formattedaddress,
        postcode: x.postcode,
        easting: x.easting,
        northing: x.northing,
        logicalStatus: x.logical_status,
        classificationCode: x.classification_code ? x.classification_code : "U",
      };
      return propObj;
    });

    searchContext.onSearchDataChange(searchContext.currentSearchData.searchString, newSearchData);
    mapContext.onSearchDataChange(searchStreets, searchLlpgStreets, searchProperties, null, null);
  };

  /**
   * event to handle when the remove from list item is clicked.
   */
  const handleRemoveFromList = async () => {
    const newStreetSearchData =
      ["street", "street&Property"].includes(variant) && streetUsrns
        ? searchContext.currentSearchData.results.filter((x) => x.type === 15 && !streetUsrns.includes(x.usrn))
        : searchContext.currentSearchData.results.filter((x) => x.type === 15);
    const newPropertySearchData =
      ["property", "street&Property"].includes(variant) && propertyUprns
        ? searchContext.currentSearchData.results.filter((x) => x.type === 24 && !propertyUprns.includes(x.uprn))
        : searchContext.currentSearchData.results.filter((x) => x.type === 24);

    updateSearchData(newStreetSearchData, newPropertySearchData);

    if (onClose) onClose();
  };

  /**
   * Event to handle the export to menu item.
   *
   * @param {Object} event The event object.
   * @param {String} format The format to export to, e.g. "XLSX", "CSV", etc.
   */
  const handleExportTo = (event, format) => {
    /**
     * Method to set the street columns in the worksheet.
     *
     * @param {Object} streetWorksheet The worksheet to set the columns for.
     * @param {Array} exportStreetData The street data to export.
     */
    const setStreetColumns = (streetWorksheet, exportStreetData) => {
      if (!settingsContext.isScottish) {
        utils.sheet_add_aoa(
          streetWorksheet,
          [
            [
              "USRN",
              "Street descriptor",
              "Language",
              "Locality",
              "Town",
              "Administrative area",
              "State",
              "State description",
              "Type",
              "Type description",
              "Street classification",
              "Street classification description",
              "Street surface",
              "Street surface description",
            ],
          ],
          {
            origin: "A1",
          }
        );
      } else {
        utils.sheet_add_aoa(
          streetWorksheet,
          [
            [
              "USRN",
              "Street descriptor",
              "Language",
              "Locality",
              "Town",
              "Island",
              "Administrative area",
              "Type",
              "Type description",
            ],
          ],
          {
            origin: "A1",
          }
        );
      }

      const usrnMaxWidth = exportStreetData.reduce((max, rec) => Math.max(max, rec.usrn?.toString().length), 10);
      const descriptorMaxWidth = exportStreetData.reduce((max, rec) => Math.max(max, rec.streetDescriptor?.length), 17);
      const localityMaxWidth = exportStreetData.reduce((max, rec) => Math.max(max, rec.locality?.length), 10);
      const townMaxWidth = exportStreetData.reduce((max, rec) => Math.max(max, rec.town?.length), 10);
      const islandMaxWidth = settingsContext.isScottish
        ? exportStreetData.reduce((max, rec) => Math.max(max, rec.island?.length), 10)
        : 0;
      const adminAreaMaxWidth = exportStreetData.reduce(
        (max, rec) => Math.max(max, rec.administrativeArea?.length),
        19
      );
      const stateDescriptionMaxWidth = exportStreetData.reduce(
        (max, rec) => Math.max(max, rec.stateDescription?.length),
        17
      );
      const typeDescriptionMaxWidth = exportStreetData.reduce(
        (max, rec) => Math.max(max, rec.typeDescription?.length),
        16
      );
      const streetClassificationDescriptionMaxWidth = exportStreetData.reduce(
        (max, rec) => Math.max(max, rec.streetClassificationDescription?.length),
        26
      );
      const streetSurfaceDescriptionMaxWidth = exportStreetData.reduce(
        (max, rec) => Math.max(max, rec.streetSurfaceDescription?.length),
        26
      );

      if (!settingsContext.isScottish) {
        streetWorksheet["!cols"] = [
          { wch: usrnMaxWidth },
          { wch: descriptorMaxWidth },
          { wch: 8 }, // Language
          { wch: localityMaxWidth },
          { wch: townMaxWidth },
          { wch: adminAreaMaxWidth },
          { wch: 5 }, // State
          { wch: stateDescriptionMaxWidth },
          { wch: 4 }, // Type
          { wch: typeDescriptionMaxWidth },
          { wch: 21 }, // Street classification
          { wch: streetClassificationDescriptionMaxWidth },
          { wch: 14 }, // Street surface
          { wch: streetSurfaceDescriptionMaxWidth },
        ];
      } else {
        streetWorksheet["!cols"] = [
          { wch: usrnMaxWidth },
          { wch: descriptorMaxWidth },
          { wch: 8 }, // Language
          { wch: localityMaxWidth },
          { wch: townMaxWidth },
          { wch: islandMaxWidth },
          { wch: adminAreaMaxWidth },
          { wch: 4 }, // Type
          { wch: typeDescriptionMaxWidth },
        ];
      }

      for (let i = 0; i < exportStreetData.length; i++) {
        streetWorksheet[`A${i + 2}`].z = "###"; // Format USRN as Number
      }
    };

    /**
     * Method to set the property columns in the worksheet.
     *
     * @param {Object} propertyWorksheet The worksheet to set the columns for.
     * @param {Array} exportPropertyData The property data to export.
     */
    const setPropertyColumns = (propertyWorksheet, exportPropertyData) => {
      if (!settingsContext.isScottish) {
        utils.sheet_add_aoa(
          propertyWorksheet,
          [
            [
              "UPRN",
              "RPC",
              "RPC description",
              "Easting",
              "Northing",
              "BLPU logical status",
              "LPI logical status",
              "LPI logical status description",
              "LPI Language",
              "Address",
              "SAO details",
              "PAO details",
              "USRN",
              "Street descriptor",
              "Street Language",
              "Post town",
              "Postcode",
              "Classification code",
              "Primary classification",
              "Secondary classification",
              "Tertiary classification",
              "Organisation",
            ],
          ],
          { origin: "A1" }
        );
      } else {
        utils.sheet_add_aoa(
          propertyWorksheet,
          [
            [
              "UPRN",
              "RPC",
              "RPC description",
              "Easting",
              "Northing",
              "BLPU logical status",
              "LPI logical status",
              "LPI logical status description",
              "LPI Language",
              "Address",
              "SAO details",
              "PAO details",
              "USRN",
              "Street descriptor",
              "Street Language",
              "Sub-locality",
              "Post town",
              "Postcode",
              "Classification code",
              "Primary classification",
              "Secondary classification",
              "Tertiary classification",
              "Organisation",
            ],
          ],
          { origin: "A1" }
        );
      }

      const uprnMaxWidth = exportPropertyData.reduce((max, rec) => Math.max(max, rec.uprn?.toString().length), 10);
      const rpcDescriptionMaxWidth = exportPropertyData.reduce(
        (max, rec) => Math.max(max, rec.rpcDescription ? rec.rpcDescription?.length : 15),
        15
      );
      const lpiStatusDescriptionMaxWidth = exportPropertyData.reduce(
        (max, rec) => Math.max(max, rec.lpiLogicalDescription?.length),
        30
      );
      const addressMaxWidth = exportPropertyData.reduce((max, rec) => Math.max(max, rec.address?.length), 10);
      const saoDetailsMaxWidth = exportPropertyData.reduce(
        (max, rec) => Math.max(max, rec.saoDetails ? rec.saoDetails?.length : 12),
        12
      );
      const paoDetailsMaxWidth = exportPropertyData.reduce((max, rec) => Math.max(max, rec.paoDetails?.length), 12);
      const usrnPropertyMaxWidth = exportPropertyData.reduce(
        (max, rec) => Math.max(max, rec.usrn?.toString().length),
        10
      );
      const streetMaxWidth = exportPropertyData.reduce((max, rec) => Math.max(max, rec.street?.length), 17);
      const subLocalityMaxWidth = settingsContext.isScottish
        ? exportPropertyData.reduce((max, rec) => Math.max(max, rec.subLocality ? rec.subLocality?.length : 12), 12)
        : 0;
      const postTownMaxWidth = exportPropertyData.reduce(
        (max, rec) => Math.max(max, rec.postTown ? rec.postTown?.length : 10),
        10
      );
      const postcodeMaxWidth = exportPropertyData.reduce(
        (max, rec) => Math.max(max, rec.postcode ? rec.postcode?.length : 10),
        10
      );
      const primaryDescriptionMaxWidth = exportPropertyData.reduce(
        (max, rec) => Math.max(max, rec.primaryClassification ? rec.primaryClassification?.length : 22),
        22
      );
      const secondaryDescriptionMaxWidth = exportPropertyData.reduce(
        (max, rec) => Math.max(max, rec.secondaryClassification ? rec.secondaryClassification?.length : 24),
        24
      );
      const tertiaryDescriptionMaxWidth = exportPropertyData.reduce(
        (max, rec) => Math.max(max, rec.tertiaryClassification ? rec.tertiaryClassification?.length : 23),
        23
      );
      const organisationMaxWidth = exportPropertyData.reduce(
        (max, rec) => Math.max(max, rec.organisation ? rec.organisation?.length : 10),
        10
      );

      if (!settingsContext.isScottish) {
        propertyWorksheet["!cols"] = [
          { wch: uprnMaxWidth },
          { wch: 3 }, // RPC
          { wch: rpcDescriptionMaxWidth },
          { wch: 12 }, // Easting
          { wch: 12 }, // Northing
          { wch: 19 }, // BLPU Logical Status
          { wch: 18 }, // LPI Logical Status
          { wch: lpiStatusDescriptionMaxWidth },
          { wch: 12 }, // LPI Language
          { wch: addressMaxWidth },
          { wch: saoDetailsMaxWidth },
          { wch: paoDetailsMaxWidth },
          { wch: usrnPropertyMaxWidth },
          { wch: streetMaxWidth },
          { wch: 15 }, // Street Language
          { wch: postTownMaxWidth },
          { wch: postcodeMaxWidth },
          { wch: 19 }, // Classification code
          { wch: primaryDescriptionMaxWidth },
          { wch: secondaryDescriptionMaxWidth },
          { wch: tertiaryDescriptionMaxWidth },
          { wch: organisationMaxWidth },
        ];
      } else {
        propertyWorksheet["!cols"] = [
          { wch: uprnMaxWidth },
          { wch: 3 }, // RPC
          { wch: rpcDescriptionMaxWidth },
          { wch: 12 }, // Easting
          { wch: 12 }, // Northing
          { wch: 19 }, // BLPU Logical Status
          { wch: 18 }, // LPI Logical Status
          { wch: lpiStatusDescriptionMaxWidth },
          { wch: 12 }, // LPI Language
          { wch: addressMaxWidth },
          { wch: saoDetailsMaxWidth },
          { wch: paoDetailsMaxWidth },
          { wch: usrnPropertyMaxWidth },
          { wch: streetMaxWidth },
          { wch: 15 }, // Street Language
          { wch: subLocalityMaxWidth },
          { wch: postTownMaxWidth },
          { wch: postcodeMaxWidth },
          { wch: 19 }, // Classification code
          { wch: primaryDescriptionMaxWidth },
          { wch: secondaryDescriptionMaxWidth },
          { wch: tertiaryDescriptionMaxWidth },
          { wch: organisationMaxWidth },
        ];
      }

      for (let i = 0; i < exportPropertyData.length; i++) {
        propertyWorksheet[`A${i + 2}`].z = "###"; // Format UPRN as Number
        propertyWorksheet[`N${i + 2}`].z = "###"; // Format USRN as Number
      }
    };

    setAnchorStreetPropertyActionsEl(null);
    setAnchorStreetActionsEl(null);
    setAnchorPropertyActionsEl(null);
    setAnchorPropertySelectionActionsEl(null);

    const workbook = utils.book_new();
    const haveStreetsToExport =
      ["street", "street&Property"].includes(variant) && streetUsrns && streetUsrns.length > 0;
    const havePropertiesToExport =
      ["property", "street&Property", "mapProperty"].includes(variant) && propertyUprns && propertyUprns.length > 0;

    if (haveStreetsToExport && havePropertiesToExport) {
      GetExportStreetData(streetUsrns, userContext, settingsContext.isScottish).then((streetData) => {
        if (streetData && streetData.length > 0) {
          const exportStreetData = streetData.sort((a, b) =>
            a.description?.localeCompare(b.description, undefined, {
              numeric: true,
              sensitivity: "base",
            })
          );
          const streetWorksheet = utils.json_to_sheet(exportStreetData);
          utils.book_append_sheet(workbook, streetWorksheet, "Streets");

          setStreetColumns(streetWorksheet, exportStreetData);

          GetExportPropertyData(propertyUprns, userContext, settingsContext.isScottish).then((propertyData) => {
            if (propertyData || propertyData.length > 0) {
              const exportPropertyData = propertyData.sort((a, b) =>
                a.address?.localeCompare(b.address, undefined, { numeric: true, sensitivity: "base" })
              );
              const propertyWorksheet = utils.json_to_sheet(exportPropertyData);
              utils.book_append_sheet(workbook, propertyWorksheet, "Properties");

              setPropertyColumns(propertyWorksheet, exportPropertyData);

              writeFile(workbook, `iManage_Cloud_${new Date().toISOString()}.${format.toLowerCase()}`, {
                compression: true,
                cellStyles: true,
              });
            } else {
              if (onError) onError("NoDataToExport");
            }
          });
        } else {
          if (onError) onError("NoDataToExport");
        }
      });
    } else if (haveStreetsToExport && !havePropertiesToExport) {
      GetExportStreetData(streetUsrns, userContext, settingsContext.isScottish).then((streetData) => {
        if (streetData && streetData.length > 0) {
          const exportStreetData = streetData.sort((a, b) =>
            a.description?.localeCompare(b.description, undefined, {
              numeric: true,
              sensitivity: "base",
            })
          );
          const streetWorksheet = utils.json_to_sheet(exportStreetData);
          utils.book_append_sheet(workbook, streetWorksheet, "Streets");

          setStreetColumns(streetWorksheet, exportStreetData);

          writeFile(workbook, `iManage_Cloud_${new Date().toISOString()}.${format.toLowerCase()}`, {
            compression: true,
            cellStyles: true,
          });
        } else {
          if (onError) onError("NoDataToExport");
        }
      });
    } else if (!haveStreetsToExport && havePropertiesToExport) {
      GetExportPropertyData(propertyUprns, userContext, settingsContext.isScottish).then((propertyData) => {
        if (propertyData && propertyData.length > 0) {
          const exportPropertyData = propertyData.sort((a, b) =>
            a.address?.localeCompare(b.address, undefined, { numeric: true, sensitivity: "base" })
          );
          const propertyWorksheet = utils.json_to_sheet(exportPropertyData);
          utils.book_append_sheet(workbook, propertyWorksheet, "Properties");

          setPropertyColumns(propertyWorksheet, exportPropertyData);

          writeFile(workbook, `iManage_Cloud_${new Date().toISOString()}.${format.toLowerCase()}`, {
            compression: true,
            cellStyles: true,
          });
        } else {
          if (onError) onError("NoDataToExport");
        }
      });
    } else if (!haveStreetsToExport && !havePropertiesToExport) {
      if (onError) onError("noRecordsSelectedToExport");
    }
  };

  /**
   * Event to handle zooming the map to a street.
   *
   * @param {Object} event The event object.
   * @param {Number} usrn The USRN of the street to zoom to.
   */
  async function zoomToStreet(event, usrn) {
    setAnchorStreetActionsEl(null);

    const found = mapContext.currentMapSearchData.streets.find((rec) => rec.usrn === usrn);
    const streetData = await GetStreetMapData(usrn, userContext, settingsContext.isScottish);

    const highlightStreet = {
      usrn: usrn,
      minX: streetData.streetStartX < streetData.streetEndX ? streetData.streetStartX : streetData.streetEndX,
      minY: streetData.streetStartY < streetData.streetEndY ? streetData.streetStartY : streetData.streetEndY,
      maxX: streetData.streetStartX > streetData.streetEndX ? streetData.streetStartX : streetData.streetEndX,
      maxY: streetData.streetStartY > streetData.streetEndY ? streetData.streetStartY : streetData.streetEndY,
    };

    if (!found) {
      const newMapSearchProperties = mapContext.currentMapSearchData.streets;
      newMapSearchProperties.push(await getStreetSearchData(streetData, settingsContext.isScottish));
    }

    mapContext.onMapChange(mapContext.currentLayers.extents, highlightStreet, null);
  }

  /**
   * Event to handle zooming the map to the streets.
   */
  const zoomToStreets = async () => {
    setAnchorStreetActionsEl(null);

    if (streetUsrns?.length > 0) {
      mapContext.onMapChange(mapContext.currentLayers.extents, null, null, { properties: null, streets: streetUsrns });
    }
  };

  /**
   * Event to handle adding the street to the activity list.
   */
  const handleAddStreetToActivityList = () => {};

  /**
   * Event to handle the display of the street context menu.
   *
   * @param {object} event The event object.
   */
  const handleStreetActionsMenuClick = (event) => {
    setAnchorStreetActionsEl(event.nativeEvent.target);
    event.stopPropagation();
  };

  /**
   * Event to handle the closing of the street context menu.
   *
   * @param {object} event The event object.
   */
  const handleStreetActionsMenuClose = (event) => {
    setAnchorStreetActionsEl(null);
    event.stopPropagation();
  };

  /**
   * Event to handle the clicking on the divide ESU button.
   *
   * @param {object} event The event object.
   */
  const handleDivideEsuClick = (event) => {
    event.stopPropagation();
    if (streetContext.esuDividedMerged) {
      if (onError) onError("streetAlreadyDividedMerged");
    }
    if (currentEsu && currentEsu.length === 1) {
      informationContext.onDisplayInformation("divideESU", "ADSSelectionControl");
      mapContext.onDivideEsu(currentEsu[0]);
    } else mapContext.onDivideEsu(null);
  };

  /**
   * Event to handle when the merge ESUs button is clicked.
   *
   * @param {object} event The event object.
   */
  const handleMergeEsusClick = (event) => {
    event.stopPropagation();
    if (streetContext.esuDividedMerged) {
      if (onError) onError("streetAlreadyDividedMerged");
    } else if (currentEsu && currentEsu.length === 2) {
      const contextStreet = currentSandboxStreet || sourceSandboxStreet;
      const originalEsu1 = contextStreet.esus.find((x) => x.esuId === currentEsu[0]);
      const originalEsu2 = contextStreet.esus.find((x) => x.esuId === currentEsu[1]);

      if (originalEsu1 && originalEsu2) {
        const esu1 = new Polyline({
          type: "polyline",
          paths:
            originalEsu1.wktGeometry && originalEsu1.wktGeometry !== ""
              ? wktToGeometry(originalEsu1.wktGeometry)
              : undefined,
          spatialReference: { wkid: spatialReferenceWkid.current },
        });

        const esu2 = new Polyline({
          type: "polyline",
          paths:
            originalEsu2.wktGeometry && originalEsu2.wktGeometry !== ""
              ? wktToGeometry(originalEsu2.wktGeometry)
              : undefined,
          spatialReference: { wkid: spatialReferenceWkid.current },
        });

        const isDisjointed = disjointOperator.execute(esu1, esu2);

        if (isDisjointed) {
          if (onError) onError("invalidMergeEsu");
        } else {
          const newEsuGeometry = unionOperator.execute(esu1, esu2);
          streetContext.onMergedEsus(currentEsu[0], currentEsu[1], newEsuGeometry);
          if (onClose) onClose();
        }
      }
    }
  };

  /**
   * Event to handle when the unassign ESU button is clicked.
   *
   * @param {object} event The event object.
   */
  const handleUnassignEsuClick = (event) => {
    event.stopPropagation();
    if (currentEsu && currentEsu.length > 0) {
      streetContext.onUnassignEsus(currentEsu);
      if (onClose) onClose();
    }
  };

  /**
   * Event to handle when the assign ESU button is clicked.
   *
   * @param {object} event The event object.
   */
  const handleAssignEsuClick = (event) => {
    event.stopPropagation();
    if (currentEsu && currentEsu.length > 0) {
      streetContext.onAssignEsus(currentEsu);
      if (onClose) onClose();
    }
  };

  /**
   * Event to handle when the create street button is clicked.
   *
   * @param {object} event The event object.
   */
  // const handleCreateStreetClick = (event) => {
  //   event.stopPropagation();
  //   if (currentEsu && currentEsu.length > 0) {
  //     streetContext.onLeavingStreet("createStreet", currentEsu);
  //   }
  // };

  /**
   * Event to handle when the delete ASD button is clicked.
   *
   * @param {object} event The event object.
   */
  const handleDeleteAsdClick = (event) => {
    event.stopPropagation();
  };

  /**
   * Event to handle adding a child record.
   */
  async function HandleAddChild() {
    if (currentUprn) {
      GetParentHierarchy(currentUprn, userContext).then((parentProperties) => {
        if (!parentProperties || parentProperties.parent.currentParentChildLevel < 4) {
          GetPropertyMapData(Number(currentUprn), userContext).then((result) => {
            propertyContext.resetPropertyErrors(false, false);
            propertyContext.onClearWizardData();
            mapContext.onWizardSetCoordinate(null);
            propertyWizardType.current = "child";
            const engLpi = result.lpis.find((x) => x.logicalStatus === result.logicalStatus && x.language === "ENG");
            propertyWizardParent.current = {
              address: engLpi.address,
              easting: result.easting,
              northing: result.northing,
              postcode: engLpi.postcode,
              uprn: currentUprn,
              usrn: engLpi.usrn,
            };
            setOpenPropertyWizard(true);
          });
        } else {
          if (onError) onError("maxParentLevel");
        }
      });
    }
  }

  /**
   * Event to handle adding a range of child properties.
   */
  async function HandleAddChildren() {
    if (currentUprn) {
      GetParentHierarchy(currentUprn, userContext).then((parentProperties) => {
        if (!parentProperties || parentProperties.parent.currentParentChildLevel < 4) {
          GetPropertyMapData(Number(currentUprn), userContext).then((result) => {
            propertyContext.resetPropertyErrors(true, true);
            propertyContext.onClearWizardData();
            mapContext.onWizardSetCoordinate(null);
            propertyWizardType.current = "rangeChildren";
            const engLpi = result.lpis.find((x) => x.logicalStatus === result.logicalStatus && x.language === "ENG");
            propertyWizardParent.current = {
              address: engLpi.address,
              easting: result.easting,
              northing: result.northing,
              postcode: engLpi.postcode,
              uprn: currentUprn,
              usrn: engLpi.usrn,
            };
            setOpenPropertyWizard(true);
          });
        } else {
          if (onError) onError("maxParentLevel");
        }
      });
    }
  }

  /**
   * Event to handle opening a property.
   */
  async function HandleOpenProperty() {
    if (currentUprn) {
      const propertyData = await GetPropertyMapData(Number(currentUprn), userContext);

      propertyContext.onPropertyChange(
        propertyData.uprn,
        0,
        propertyData.address,
        propertyData.address,
        propertyData.postcode,
        propertyData.easting,
        propertyData.northing,
        false,
        null
      );
      const foundProperty = mapContext.currentMapSearchData.properties.find(
        ({ uprn }) => Number(uprn) === Number(propertyData.uprn)
      );
      const currentSearchProperties = mapContext.currentMapSearchData.properties;

      if (!foundProperty) {
        currentSearchProperties.push({
          uprn: propertyData.uprn,
          parentUprn: propertyData.parentUprn,
          address: propertyData.address,
          formattedAddress: propertyData.address,
          postcode: propertyData.postcode,
          easting: propertyData.easting,
          northing: propertyData.northing,
          logicalStatus: propertyData.logicalStatus,
          classificationCode: getClassificationCode(propertyData),
        });
      }
      const extents = propertyData
        ? propertyData.blpuProvenances.map((rec) => ({
            pkId: rec.pkId,
            uprn: propertyData.uprn,
            code: rec.provenanceCode,
            historical: !!rec.endDate,
            geometry: rec.wktGeometry && rec.wktGeometry !== "" ? wktToGeometry(rec.wktGeometry) : undefined,
          }))
        : undefined;

      mapContext.onSearchDataChange(
        mapContext.currentMapSearchData.streets,
        mapContext.currentMapSearchData.llpgStreets,
        currentSearchProperties,
        null,
        propertyData.uprn
      );
      mapContext.onMapChange(extents, null, null);
    }
  }

  /**
   * Method to zoom to a given property.
   *
   * @param {object} event The event object.
   * @param {number} uprn The UPRN of the property.
   */
  const zoomToProperty = (event, uprn) => {
    setAnchorPropertyActionsEl(null);

    event.stopPropagation();

    const found = mapContext.currentMapSearchData.properties.find((rec) => Number(rec.uprn) === Number(uprn));

    if (found) {
      mapContext.onMapChange(mapContext.currentLayers.extents, null, uprn);
    }
  };

  /**
   * Event to handle zooming the map to the properties.
   */
  const zoomToProperties = () => {
    setAnchorPropertyActionsEl(null);

    if (propertyUprns?.length > 0) {
      mapContext.onMapChange(mapContext.currentLayers.extents, null, null, {
        properties: propertyUprns,
        streets: null,
      });
    }
  };

  /**
   * Event to handle zooming the map to the streets and properties.
   */
  const zoomToStreetsAndProperties = () => {
    setAnchorStreetPropertyActionsEl(null);

    if (streetUsrns?.length > 0 || propertyUprns?.length > 0) {
      mapContext.onMapChange(mapContext.currentLayers.extents, null, null, {
        properties: propertyUprns?.length > 0 ? propertyUprns : null,
        streets: streetUsrns?.length > 0 ? streetUsrns : null,
      });
    }
  };

  /**
   * Event to handle when the make child of menu item is clicked.
   */
  const handleMakeChildOf = () => {
    setAnchorPropertyActionsEl(null);

    if (selectionCount === 1) {
      setMakeChildUprn([Number(currentUprn)]);
    } else {
      setMakeChildUprn(propertyUprns);
    }
    setOpenMakeChild(true);
    searchContext.onHideSearch(true);
  };

  /**
   * Event to handle when the set approved button is clicked
   */
  const handleSetApprovedButtonClicked = () => {
    setEditLogicalStatusVariant("approved");
    setMultiEditUprns([...new Set(propertyUprns)]);
    setOpenEditLogicalStatus(true);
  };

  /**
   * Event to handle adding a property to the activity list.
   */
  const handleAddPropertyToActivityList = () => {};

  /**
   * Event to handle when the move button is clicked.
   */
  const handleMoveBlpuButtonClicked = () => {
    setMultiEditUprns([...new Set(propertyUprns)]);
    setOpenMoveBlpu(true);
    mapContext.onDisplayWizardMap(true);
  };

  /**
   * Method to reject a property.
   */
  async function RejectProperty() {
    if (currentUprn) {
      await HandleOpenProperty();

      propertyContext.onLogicalStatusChange(9);

      const found = mapContext.currentMapSearchData.properties.find((x) => Number(x.uprn) === Number(currentUprn));

      if (found) {
        const updatedProperty = {
          uprn: found.uprn,
          parentUprn: found.parentUprn,
          address: found.address,
          formattedAddress: found.formattedAddress,
          postcode: found.postcode,
          easting: found.easting,
          northing: found.northing,
          logicalStatus: 9,
          classificationCode: found.classificationCode,
        };

        const currentSearchProperties = mapContext.currentMapSearchData.properties.map(
          (x) => [updatedProperty].find((rec) => Number(rec.uprn) === Number(x.uprn)) || x
        );

        mapContext.onSearchDataChange(
          mapContext.currentMapSearchData.streets,
          mapContext.currentMapSearchData.llpgStreets,
          currentSearchProperties,
          null,
          Number(currentUprn)
        );
      }
    }
  }

  /**
   * Method to historicise a property.
   */
  async function HistoriciseProperty() {
    if (currentUprn) {
      await HandleOpenProperty();

      propertyContext.onLogicalStatusChange(8);

      const found = mapContext.currentMapSearchData.properties.find((x) => Number(x.uprn) === Number(currentUprn));

      if (found) {
        const updatedProperty = {
          uprn: found.uprn,
          parentUprn: found.parentUprn,
          address: found.address,
          formattedAddress: found.formattedAddress,
          postcode: found.postcode,
          easting: found.easting,
          northing: found.northing,
          logicalStatus: 8,
          classificationCode: found.classificationCode,
        };

        const currentSearchProperties = mapContext.currentMapSearchData.properties.map(
          (x) => [updatedProperty].find((rec) => Number(rec.uprn) === Number(x.uprn)) || x
        );

        mapContext.onSearchDataChange(
          mapContext.currentMapSearchData.streets,
          mapContext.currentMapSearchData.llpgStreets,
          currentSearchProperties,
          null,
          Number(currentUprn)
        );
      }
    }
  }

  /**
   * Event to handle when the set approved menu item is clicked
   */
  const handleSetApprovedClick = () => {
    setAnchorPropertyActionsEl(null);
    setEditLogicalStatusVariant("approved");
    setMultiEditUprns([...new Set(propertyUprns)]);
    setOpenEditLogicalStatus(true);
  };

  /**
   * Event to handle when the set historic menu item is clicked
   */
  const handleSetHistoricClick = () => {
    setAnchorPropertyActionsEl(null);
    setEditLogicalStatusVariant("historic");
    setMultiEditUprns([...new Set(propertyUprns)]);
    setOpenEditLogicalStatus(true);
  };

  /**
   * Method to handle displaying the single field multi-edit dialog.
   *
   * @param {string} variant The single field that should be edited/added.
   */
  const displaySingleFieldDialog = (variant) => {
    setAnchorPropertyActionsEl(null);
    setEditSingleFieldVariant(variant);
    setMultiEditUprns([...new Set(propertyUprns)]);
    setOpenEditSingleField(true);
  };

  /**
   * Event to handle when the edit classification menu item is clicked
   */
  const handleEditClassificationClick = () => {
    if (!settingsContext.isScottish) displaySingleFieldDialog("classification");
    else {
      setAnchorPropertyActionsEl(null);
      setMultiEditUprns([...new Set(propertyUprns)]);
      setOpenAddClassification(true);
    }
  };

  /**
   * Event to handle when the edit RPC menu item is clicked
   */
  const handleEditRpcClick = () => {
    displaySingleFieldDialog("rpc");
  };

  /**
   * Event to handle when the edit state menu item is clicked
   */
  const handleEditStateClick = () => {
    displaySingleFieldDialog("state");
  };

  /**
   * Event to handle when the edit level menu item is clicked
   */
  const handleEditLevelClick = () => {
    displaySingleFieldDialog("level");
  };

  /**
   * Event to handle when the edit exclude from export menu item is clicked
   */
  const handleEditExcludeFromExportClick = () => {
    displaySingleFieldDialog("excludeFromExport");
  };

  /**
   * Event to handle when the edit site visit required menu item is clicked
   */
  const handleEditSiteVisitRequiredClick = () => {
    displaySingleFieldDialog("siteVisitRequired");
  };

  /**
   * Event to handle when the add note menu item is clicked
   */
  const handleAddNoteClick = () => {
    displaySingleFieldDialog("note");
  };

  /**
   * Event to handle when the move BLPU seed point menu item is clicked
   */
  const handleMoveBlpuClick = () => {
    setAnchorPropertyActionsEl(null);
    setOpenMoveBlpu(true);
    mapContext.onDisplayWizardMap(true);
  };

  /**
   * Event to handle when the edit address fields menu item is clicked
   */
  const handleEditAddressFieldsClick = () => {
    setAnchorPropertyActionsEl(null);
    setMultiEditUprns([...new Set(propertyUprns)]);
    setOpenEditAddressFields(true);
  };

  /**
   * Event to handle when the plot to postal menu item is clicked
   */
  const handlePlotToPostalWizardClick = () => {
    setAnchorPropertyActionsEl(null);
    setMultiEditUprns([...new Set(propertyUprns)]);
    setOpenPlotToPostalWizard(true);
  };

  /**
   * Event to handle when the add cross reference menu item is clicked
   */
  const handleAddCrossReferenceClick = () => {
    setAnchorPropertyActionsEl(null);
    setMultiEditUprns([...new Set(propertyUprns)]);
    setOpenAddCrossReference(true);
  };

  /**
   * Event to handle when the remove cross reference menu item is clicked
   */
  const handleRemoveCrossReferenceClick = () => {
    setAnchorPropertyActionsEl(null);
    setMultiEditUprns([...new Set(propertyUprns)]);
    setOpenRemoveCrossReference(true);
  };

  /**
   * Event to handle when the create new list button is clicked
   */
  const handleCreateNewList = async () => {
    const newStreetSearchData =
      ["street", "street&Property"].includes(variant) && streetUsrns
        ? await getStreetListDetails(streetUsrns, userContext)
        : [];

    const newPropertySearchData =
      ["property", "street&Property", "mapProperty"].includes(variant) && propertyUprns
        ? await getPropertyListDetails(propertyUprns, userContext)
        : [];

    updateSearchData(newStreetSearchData, newPropertySearchData);
    selectionContext.onCloseSelection();
  };

  /**
   * Event to handle when the add to existing list button is clicked
   */
  const handleAddToExistingList = () => {
    handlePropertiesSelected("existing");
  };

  const handlePropertiesSelected = async (action) => {
    const newSearchData = await getPropertyListDetails(propertyUprns, userContext);

    if (newSearchData) searchContext.onPropertiesSelected(action, newSearchData);
    selectionContext.onCloseSelection();

    if (onClose) onClose();
  };

  /**
   * Event to handle deleting a classification
   */
  const handleDeleteClassification = () => {
    if (currentClassification && currentClassification.length > 0 && onDeleteClassification) onDeleteClassification();
  };

  /**
   * Event to handle deleting a organisation
   */
  const handleDeleteOrganisation = () => {
    if (currentOrganisation && currentOrganisation.length > 0 && onDeleteOrganisation) onDeleteOrganisation();
  };

  /**
   * Event to handle deleting a successor cross reference
   */
  const handleDeleteSuccessorCrossRef = () => {
    if (currentSuccessorCrossRef && currentSuccessorCrossRef.length > 0 && onDeleteSuccessorCrossRef)
      onDeleteSuccessorCrossRef();
  };

  /**
   * Event to handle deleting a provenance
   */
  const handleDeleteProvenance = () => {
    if (currentProvenance && currentProvenance.length > 0 && onDeleteProvenance) onDeleteProvenance();
  };

  /**
   * Event to handle merging extents
   */
  const handleMergeExtent = () => {
    if (currentExtent && currentExtent.length > 0 && onMergeExtent) onMergeExtent();
  };

  /**
   * Event to handle deleting a cross reference
   */
  const handleDeleteCrossReference = () => {
    if (currentCrossReference && currentCrossReference.length > 0 && onDeleteCrossReference) onDeleteCrossReference();
  };

  /**
   * Event to handle adding a note.
   */
  const handleAddNote = () => {
    setAnchorWizardActionsEl(null);
    if (onAddNote) onAddNote();
  };

  /**
   * Event to handle editing the classification.
   */
  const handleEditClassification = () => {
    setAnchorWizardActionsEl(null);
    if (onEditWizard) onEditWizard("classification");
  };

  /**
   * Event to handle editing the exclude from export.
   */
  const handleEditExcludeFromExport = () => {
    setAnchorWizardActionsEl(null);
    if (onEditWizard) onEditWizard("excludeFromExport");
  };

  /**
   * Event to handle editing the site visit.
   */
  const handleEditSiteVisit = () => {
    setAnchorWizardActionsEl(null);
    if (onEditWizard) onEditWizard("siteVisit");
  };

  /**
   * Event to handle editing the level.
   */
  const handleEditLevel = () => {
    setAnchorWizardActionsEl(null);
    if (onEditWizard) onEditWizard("level");
  };

  /**
   * Event to handle editing the postcode.
   */
  const handleEditPostcode = () => {
    setAnchorWizardActionsEl(null);
    if (onEditWizard) onEditWizard("postcode");
  };

  /**
   * Event to handle editing the post town.
   */
  const handleEditPostTown = () => {
    setAnchorWizardActionsEl(null);
    if (onEditWizard) onEditWizard("postTown");
  };

  /**
   * Event to handle editing the sub-locality.
   */
  const handleEditSubLocality = () => {
    setAnchorWizardActionsEl(null);
    if (onEditWizard) onEditWizard("subLocality");
  };

  /**
   * Event to handle editing the RPC.
   */
  const handleEditRpc = () => {
    setAnchorWizardActionsEl(null);
    if (onEditWizard) onEditWizard("rpc");
  };

  /**
   * Event to handle the delete wizard.
   */
  const handleDeleteWizard = () => {
    setAnchorWizardActionsEl(null);
    if (onDeleteWizard) onDeleteWizard();
  };

  /**
   * Method to get the menu anchor based on the event.
   *
   * @param {Object} element The button element
   * @returns {DOMRect} The DOMRect representing the menu anchor position.
   */
  const getMenuAnchorPosition = (element) => {
    if (!element) return null;

    const menuButtonClientRec = element.getBoundingClientRect();
    return { top: menuButtonClientRec.bottom + 12, left: menuButtonClientRec.left };
  };

  /**
   * Event to handle displaying the property context menu.
   *
   * @param {object} event The event object.
   */
  const handlePropertyActionsMenuClick = (event) => {
    setAnchorPropertyActionsEl(event.nativeEvent.target);
    event.stopPropagation();
  };

  /**
   * Event to handle the closing of the property context menu.
   *
   * @param {object} event The event object.
   */
  const handlePropertyActionsMenuClose = (event) => {
    setAnchorPropertyActionsEl(null);
    event.stopPropagation();
  };

  /**
   * Event to handle displaying the street & property context menu.
   *
   * @param {object} event The event object.
   */
  const handleStreetPropertyActionsMenuClick = (event) => {
    setAnchorStreetPropertyActionsEl(event.nativeEvent.target);
    event.stopPropagation();
  };

  /**
   * Event to handle the closing of the street & property context menu.
   *
   * @param {object} event The event object.
   */
  const handleStreetPropertyActionsMenuClose = (event) => {
    setAnchorStreetPropertyActionsEl(null);
    event.stopPropagation();
  };

  /**
   * Event to handle displaying the property selection context menu.
   *
   * @param {object} event The event object.
   */
  const handlePropertySelectionActionsMenuClick = (event) => {
    setAnchorPropertySelectionActionsEl(event.nativeEvent.target);
    event.stopPropagation();
  };

  /**
   * Event to handle the closing of the property selection context menu.
   *
   * @param {object} event The event object.
   */
  const handlePropertySelectionActionsMenuClose = (event) => {
    setAnchorPropertySelectionActionsEl(null);
    event.stopPropagation();
  };

  /**
   * Event to handle displaying the wizard context menu.
   *
   * @param {object} event The event object.
   */
  const handleWizardActionsMenuClick = (event) => {
    setAnchorWizardActionsEl(event.nativeEvent.target);
    event.stopPropagation();
  };

  /**
   * Event to handle the closing of the wizard context menu.
   *
   * @param {object} event The event object.
   */
  const handleWizardActionsMenuClose = (event) => {
    setAnchorWizardActionsEl(null);
    event.stopPropagation();
  };

  /**
   * Method used to copy the text to the clipboard.
   *
   * @param {object} event The event object.
   * @param {string|null} text The text that needs to be copied to the clipboard.
   * @param {string} dataType The type of data that is being copied to the clipboard.
   */
  const itemCopy = (event, text, dataType) => {
    event.stopPropagation();
    if (text) {
      copyTextToClipboard(text);
      if (onSetCopyOpen) onSetCopyOpen(dataType);
    }
  };

  /**
   * Display the street/property in Google Street View.
   *
   * @param {object} event The event object
   * @param {object} rec The record that needs to be displayed in Google Street View.
   */
  async function handleStreetViewClick(event, dataId, dataType) {
    if (dataId) {
      if (dataType === "USRN") {
        handleStreetActionsMenuClose(event);

        await GetStreetMapData(dataId, userContext, settingsContext.isScottish).then((result) => {
          if (result && result.esus && result.esus.length > 0) {
            const esuPoints = result.esus[0].wktGeometry.replace("LINESTRING (", "").replace(")").split(",");

            if (esuPoints && esuPoints.length > 0) {
              const firstEsuPointString = esuPoints[0].split(" ");
              if (firstEsuPointString) {
                const bngPoint = [Number(firstEsuPointString[0]), Number(firstEsuPointString[1])];
                openInStreetView(bngPoint, spatialReferenceWkid.current);
              }
            }
          }
        });
      } else {
        handlePropertyActionsMenuClose(event);
        const found = mapContext.currentMapSearchData.properties.find((rec) => Number(rec.uprn) === Number(dataId));
        if (found && found.easting && found.northing)
          openInStreetView([found.easting, found.northing], spatialReferenceWkid.current);
      }
    }
  }

  /**
   * Method to get the styling for the control.
   *
   * @returns {object} The styling for the control.
   */
  const getControlStyle = () => {
    return {
      boxShadow: `4px 4px 7px ${adsLightGreyA50}`,
      borderRadius: "9px",
      backgroundColor: adsWhite,
      zIndex: 1,
    };
  };

  useEffect(() => {
    let numTypes = 0;
    if (variant === "street&Property") numTypes = 2;
    else if (variant !== "unknown") numTypes = 1;

    setNumberOfTypes(numTypes);
  }, [variant]);

  useEffect(() => {
    if (currentStreet && currentStreet.id !== currentUsrn) {
      const checkIfStreetClosed = async (usrn) => {
        const streetClosed = await IsStreetClosed(usrn, userContext, settingsContext.isScottish);
        setCurrentStreetClosed(streetClosed);
      };

      setCurrentUsrn(currentStreet.id);
      checkIfStreetClosed(currentStreet.id);
    } else if (currentUsrn) {
      setCurrentUsrn(null);
      setCurrentStreetClosed(false);
    }
  }, [currentStreet, settingsContext.isScottish, userContext, currentUsrn]);

  useEffect(() => {
    if (currentProperty) setCurrentUprn(currentProperty.id);
    else setCurrentUprn(null);
  }, [currentProperty]);

  useEffect(() => {
    if (userCanEditStreet !== (userContext.currentUser && userContext.currentUser.editStreet))
      setUserCanEditStreet(userContext.currentUser && userContext.currentUser.editStreet);
    if (userCanEditASD !== (userContext.currentUser && userContext.currentUser.editASD))
      setUserCanEditASD(userContext.currentUser && userContext.currentUser.editASD);
    if (userCanEditProperty !== (userContext.currentUser && userContext.currentUser.editProperty))
      setUserCanEditProperty(userContext.currentUser && userContext.currentUser.editProperty);
  }, [userCanEditASD, userCanEditProperty, userCanEditStreet, userContext.currentUser]);

  useEffect(() => {
    if (informationContext.informationSource && informationContext.informationSource === "ADSSelectionControl")
      setInformationAnchorEl(document.getElementById("ads-street-esu-list-tab"));
    else setInformationAnchorEl(null);
  }, [informationContext.informationSource]);

  return (
    <>
      <Box id="ads-selection-control" sx={getControlStyle()}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ padding: "4px 12px 4px 0px" }}>
          <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            spacing={1}
            sx={{ paddingRight: "32px" }}
          >
            <IconButton onClick={handleCloseClick} size="small" title="Clear selection">
              <ClearIcon sx={{ backgroundColor: adsBlueA, color: adsWhite }} />
            </IconButton>
            <Typography variant="body2" noWrap>{`${selectionCount} selected`}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
            {numberOfTypes > 1 &&
              (numberOfTypes === 2 && variant === "street&Property" ? (
                <>
                  <ADSSelectionButton
                    variant="createList"
                    selectionCount={selectionCount}
                    userCanEdit
                    onClick={handleCreateNewList}
                  />
                  <ADSSelectionButton
                    variant="removeFromList"
                    selectionCount={selectionCount}
                    userCanEdit
                    onClick={handleRemoveFromList}
                  />
                  <ADSSelectionButton
                    variant="more"
                    selectionCount={selectionCount}
                    menuControlId="street-property-actions-menu"
                    onClick={handleStreetPropertyActionsMenuClick}
                  />
                  <Menu
                    id="street-property-actions-menu"
                    elevation={2}
                    anchorReference="anchorPosition"
                    anchorPosition={getMenuAnchorPosition(anchorStreetPropertyActionsEl)}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                    keepMounted
                    open={Boolean(anchorStreetPropertyActionsEl)}
                    onClose={handleStreetPropertyActionsMenuClose}
                    sx={menuStyle}
                  >
                    <MenuItem dense onClick={zoomToStreetsAndProperties} sx={menuItemStyle(true)}>
                      <Typography variant="body2">Zoom to these</Typography>
                    </MenuItem>
                    <MenuItem dense onClick={(event) => handleExportTo(event, "XLSX")} sx={menuItemStyle(false)}>
                      <Typography variant="body2">Export to ...</Typography>
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Typography variant="body2" color="error">
                  No shared actions
                </Typography>
              ))}
            {numberOfTypes === 1 && variant === "street" && (
              <>
                {selectionCount === 1 && (
                  <ADSSelectionButton
                    variant="zoom"
                    selectionCount={selectionCount}
                    userCanEdit
                    onClick={(event) => zoomToStreet(event, Number(currentUsrn))}
                  />
                )}
                {process.env.NODE_ENV === "development" && (
                  <ADSSelectionButton
                    variant="addList"
                    selectionCount={selectionCount}
                    onClick={handleAddStreetToActivityList}
                  />
                )}
                <ADSSelectionButton
                  variant="more"
                  selectionCount={selectionCount}
                  menuControlId="street-actions-menu"
                  onClick={handleStreetActionsMenuClick}
                />
                {selectionCount === 1 ? (
                  <Menu
                    id="single-street-actions-menu"
                    elevation={2}
                    anchorReference="anchorPosition"
                    anchorPosition={getMenuAnchorPosition(anchorStreetActionsEl)}
                    // anchorEl={anchorStreetActionsEl}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                    keepMounted
                    open={Boolean(anchorStreetActionsEl)}
                    onClose={handleStreetActionsMenuClose}
                    sx={menuStyle}
                  >
                    {userCanEditProperty && !currentStreetClosed && (
                      <MenuItem
                        dense
                        disabled={currentStreet && ![11, 12, 19].includes(currentStreet.logical_status)}
                        onClick={HandleAddProperty}
                        sx={menuItemStyle(false)}
                      >
                        <Typography variant="body2">Add property</Typography>
                      </MenuItem>
                    )}
                    {userCanEditProperty && !currentStreetClosed && (
                      <MenuItem
                        dense
                        divider
                        disabled={currentStreet && ![11, 12, 19].includes(currentStreet.logical_status)}
                        onClick={HandleAddRange}
                        sx={menuItemStyle(true)}
                      >
                        <Typography variant="body2">Add properties</Typography>
                      </MenuItem>
                    )}
                    <MenuItem dense onClick={(event) => itemCopy(event, currentUsrn, "USRN")} sx={menuItemStyle(false)}>
                      <Typography variant="body2">Copy USRN</Typography>
                    </MenuItem>
                    <MenuItem
                      dense
                      onClick={(event) => handleStreetViewClick(event, currentUsrn, "USRN")}
                      sx={menuItemStyle(false)}
                    >
                      <Typography variant="body2">Open in Street View</Typography>
                    </MenuItem>
                    {process.env.NODE_ENV === "development" && (
                      <MenuItem dense disabled sx={menuItemStyle(false)}>
                        <Typography variant="body2">Search nearby</Typography>
                      </MenuItem>
                    )}
                    <MenuItem
                      dense
                      divider
                      onClick={(event) => zoomToStreet(event, Number(currentUsrn))}
                      sx={menuItemStyle(true)}
                    >
                      <Typography variant="body2">Zoom to this</Typography>
                    </MenuItem>
                    {process.env.NODE_ENV === "development" && (
                      <MenuItem dense disabled sx={menuItemStyle(false)}>
                        <Typography variant="body2">Bookmark</Typography>
                      </MenuItem>
                    )}
                    {process.env.NODE_ENV === "development" && (
                      <MenuItem dense disabled sx={menuItemStyle(true)}>
                        <Typography variant="body2">Add to list</Typography>
                      </MenuItem>
                    )}
                    <MenuItem dense onClick={(event) => handleExportTo(event, "XLSX")} sx={menuItemStyle(false)}>
                      <Typography variant="body2">Export to ...</Typography>
                    </MenuItem>
                    <MenuItem dense divider onClick={handleRemoveFromList} sx={menuItemStyle(true)}>
                      <Typography variant="body2">Remove from list</Typography>
                    </MenuItem>
                    {process.env.NODE_ENV === "development" && userCanEditStreet && (
                      <MenuItem dense disabled sx={menuItemStyle(false)}>
                        <Typography variant="body2">Close street</Typography>
                      </MenuItem>
                    )}
                    {process.env.NODE_ENV === "development" && !settingsContext.isScottish && userCanEditStreet && (
                      <MenuItem dense disabled sx={menuItemStyle(false)}>
                        <Typography variant="inherit" color="error">
                          Delete
                        </Typography>
                      </MenuItem>
                    )}
                  </Menu>
                ) : (
                  <Menu
                    id="multiple-street-actions-menu"
                    elevation={2}
                    anchorReference="anchorPosition"
                    anchorPosition={getMenuAnchorPosition(anchorStreetActionsEl)}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                    keepMounted
                    open={Boolean(anchorStreetActionsEl)}
                    onClose={handleStreetActionsMenuClose}
                    sx={menuStyle}
                  >
                    <MenuItem dense onClick={zoomToStreets} sx={menuItemStyle(true)}>
                      <Typography variant="body2">Zoom to these</Typography>
                    </MenuItem>
                    <MenuItem dense onClick={(event) => handleExportTo(event, "XLSX")} sx={menuItemStyle(false)}>
                      <Typography variant="body2">Export to ...</Typography>
                    </MenuItem>
                    {process.env.NODE_ENV === "development" && (
                      <MenuItem dense disabled sx={menuItemStyle(false)}>
                        <Typography variant="body2">Search nearby</Typography>
                      </MenuItem>
                    )}
                    {process.env.NODE_ENV === "development" && (
                      <MenuItem dense disabled sx={menuItemStyle(false)}>
                        <Typography variant="body2">Bookmark</Typography>
                      </MenuItem>
                    )}
                    {process.env.NODE_ENV === "development" && (
                      <MenuItem dense disabled sx={menuItemStyle(true)}>
                        <Typography variant="body2">Add to list</Typography>
                      </MenuItem>
                    )}
                    <MenuItem dense divider onClick={handleRemoveFromList} sx={menuItemStyle(true)}>
                      <Typography variant="body2">Remove from list</Typography>
                    </MenuItem>
                    {process.env.NODE_ENV === "development" && userCanEditStreet && (
                      <MenuItem dense disabled sx={menuItemStyle(false)}>
                        <Typography variant="body2">Close street</Typography>
                      </MenuItem>
                    )}
                    {process.env.NODE_ENV === "development" && !settingsContext.isScottish && userCanEditStreet && (
                      <MenuItem dense disabled sx={menuItemStyle(false)}>
                        <Typography variant="inherit" color="error">
                          Delete
                        </Typography>
                      </MenuItem>
                    )}
                  </Menu>
                )}
              </>
            )}
            {numberOfTypes === 1 && variant === "esu" && (
              <>
                {selectionCount === 1 && userCanEditStreet ? (
                  <ADSSelectionButton
                    variant="divide"
                    selectionCount={selectionCount}
                    isSelected={informationOpen}
                    onClick={handleDivideEsuClick}
                  />
                ) : selectionCount === 2 && userCanEditStreet ? (
                  <ADSSelectionButton variant="merge" selectionCount={selectionCount} onClick={handleMergeEsusClick} />
                ) : null}
                {userCanEditStreet && (
                  <ADSSelectionButton
                    variant="unassign"
                    selectionCount={selectionCount}
                    onClick={handleUnassignEsuClick}
                  />
                )}
              </>
            )}
            {numberOfTypes === 1 && variant === "mapEsu" && userCanEditStreet && (
              <ADSSelectionButton variant="assign" selectionCount={selectionCount} onClick={handleAssignEsuClick} />
            )}
            {numberOfTypes === 1 && variant === "asd" && userCanEditASD && (
              <ADSSelectionButton
                variant="deleteAsd"
                isDisabled
                selectionCount={selectionCount}
                onClick={handleDeleteAsdClick}
              />
            )}
            {numberOfTypes === 1 && variant === "property" && (
              <>
                {selectionCount === 1 && (
                  <ADSSelectionButton
                    variant="zoom"
                    selectionCount={selectionCount}
                    onClick={(event) => zoomToProperty(event, currentUprn)}
                  />
                )}
                <ADSSelectionButton
                  variant="approved"
                  selectionCount={selectionCount}
                  onClick={handleSetApprovedButtonClicked}
                />
                {process.env.NODE_ENV === "development" && (
                  <ADSSelectionButton
                    variant="addList"
                    isDisabled
                    selectionCount={selectionCount}
                    onClick={handleAddPropertyToActivityList}
                  />
                )}
                <ADSSelectionButton
                  variant="moveBlpu"
                  selectionCount={selectionCount}
                  onClick={handleMoveBlpuButtonClicked}
                />
                <ADSSelectionButton
                  variant="more"
                  selectionCount={selectionCount}
                  menuControlId="property-actions-menu"
                  onClick={handlePropertyActionsMenuClick}
                />
                {selectionCount === 1 ? (
                  <Menu
                    id="single-property-actions-menu"
                    elevation={2}
                    anchorReference="anchorPosition"
                    anchorPosition={getMenuAnchorPosition(anchorPropertyActionsEl)}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                    keepMounted
                    open={Boolean(anchorPropertyActionsEl)}
                    onClose={handlePropertyActionsMenuClose}
                    sx={menuStyle}
                  >
                    {userCanEditProperty && (
                      <MenuItem
                        dense
                        disabled={currentProperty && currentProperty.logical_status > 6}
                        onClick={HandleAddChild}
                        sx={menuItemStyle(false)}
                      >
                        <Typography variant="body2">Add child</Typography>
                      </MenuItem>
                    )}
                    {userCanEditProperty && (
                      <MenuItem
                        dense
                        divider
                        disabled={currentProperty && currentProperty.logical_status > 6}
                        onClick={HandleAddChildren}
                        sx={menuItemStyle(true)}
                      >
                        <Typography variant="body2">Add children</Typography>
                      </MenuItem>
                    )}
                    <MenuItem dense onClick={(event) => itemCopy(event, currentUprn, "UPRN")} sx={menuItemStyle(false)}>
                      <Typography variant="body2">Copy UPRN</Typography>
                    </MenuItem>
                    <MenuItem
                      dense
                      divider
                      onClick={(event) => itemCopy(event, currentAddress, "Address")}
                      sx={menuItemStyle(true)}
                    >
                      <Typography variant="body2">Copy address</Typography>
                    </MenuItem>
                    <MenuItem
                      dense
                      onClick={(event) => handleStreetViewClick(event, currentUprn, "UPRN")}
                      sx={menuItemStyle(false)}
                    >
                      <Typography variant="body2">Open in Street View</Typography>
                    </MenuItem>
                    {process.env.NODE_ENV === "development" && (
                      <MenuItem dense disabled sx={menuItemStyle(false)}>
                        <Typography variant="body2">Search nearby</Typography>
                      </MenuItem>
                    )}
                    <MenuItem
                      dense
                      divider
                      onClick={(event) => zoomToProperty(event, currentUprn)}
                      sx={menuItemStyle(true)}
                    >
                      <Typography variant="body2">Zoom to this</Typography>
                    </MenuItem>
                    {process.env.NODE_ENV === "development" && (
                      <MenuItem dense disabled sx={menuItemStyle(false)}>
                        <Typography variant="body2">Bookmark</Typography>
                      </MenuItem>
                    )}
                    {process.env.NODE_ENV === "development" && (
                      <MenuItem dense disabled sx={menuItemStyle(false)}>
                        <Typography variant="body2">Add to list</Typography>
                      </MenuItem>
                    )}
                    <MenuItem dense divider onClick={handleRemoveFromList} sx={menuItemStyle(true)}>
                      <Typography variant="body2">Remove from list</Typography>
                    </MenuItem>
                    <MenuItem dense onClick={(event) => handleExportTo(event, "XLSX")} sx={menuItemStyle(false)}>
                      <Typography variant="body2">Export to ...</Typography>
                    </MenuItem>
                    {process.env.NODE_ENV === "development" && userCanEditProperty && (
                      <MenuItem dense disabled sx={menuItemStyle(false)}>
                        <Typography variant="body2">Move street</Typography>
                      </MenuItem>
                    )}
                    {userCanEditProperty && (
                      <MenuItem dense onClick={handleMakeChildOf} sx={menuItemStyle(false)}>
                        <Typography variant="body2">Make child of...</Typography>
                      </MenuItem>
                    )}
                    {userCanEditProperty && (
                      <MenuItem dense divider onClick={handleMoveBlpuClick} sx={menuItemStyle(true)}>
                        <Typography variant="body2">Move seed point</Typography>
                      </MenuItem>
                    )}
                    {userCanEditProperty && (
                      <MenuItem dense onClick={RejectProperty} sx={menuItemStyle(false)}>
                        <Typography variant="body2">Reject</Typography>
                      </MenuItem>
                    )}
                    {userCanEditProperty && (
                      <MenuItem dense onClick={HistoriciseProperty} sx={menuItemStyle(false)}>
                        <Typography variant="body2">Historicise</Typography>
                      </MenuItem>
                    )}
                    {process.env.NODE_ENV === "development" && userCanEditProperty && (
                      <MenuItem dense disabled sx={menuItemStyle(false)}>
                        <Typography variant="inherit" color="error">
                          Delete
                        </Typography>
                      </MenuItem>
                    )}
                  </Menu>
                ) : (
                  <Menu
                    id="multi-property-actions-menu"
                    elevation={2}
                    anchorReference="anchorPosition"
                    anchorPosition={getMenuAnchorPosition(anchorPropertyActionsEl)}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                    keepMounted
                    open={Boolean(anchorPropertyActionsEl)}
                    onClose={handlePropertyActionsMenuClose}
                    sx={menuStyle}
                  >
                    {userCanEditProperty && (
                      <MenuItem dense onClick={handleSetApprovedClick} sx={menuItemStyle(false)}>
                        <Typography variant="body2">Set approved</Typography>
                      </MenuItem>
                    )}
                    {userCanEditProperty && (
                      <MenuItem dense divider onClick={handleSetHistoricClick} sx={menuItemStyle(true)}>
                        <Typography variant="body2">Set historic</Typography>
                      </MenuItem>
                    )}
                    {userCanEditProperty && (
                      <MenuItem dense onClick={handleEditClassificationClick} sx={menuItemStyle(false)}>
                        <Typography variant="body2">Edit classification</Typography>
                      </MenuItem>
                    )}
                    {userCanEditProperty && (
                      <MenuItem dense onClick={handleEditRpcClick} sx={menuItemStyle(false)}>
                        <Typography variant="body2">Edit RPC</Typography>
                      </MenuItem>
                    )}
                    {userCanEditProperty && (
                      <MenuItem dense onClick={handleEditStateClick} sx={menuItemStyle(false)}>
                        <Typography variant="body2">Edit state</Typography>
                      </MenuItem>
                    )}
                    {userCanEditProperty && (
                      <MenuItem dense onClick={handleEditLevelClick} sx={menuItemStyle(false)}>
                        <Typography variant="body2">Edit level</Typography>
                      </MenuItem>
                    )}
                    {userCanEditProperty && (
                      <MenuItem dense onClick={handleEditExcludeFromExportClick} sx={menuItemStyle(false)}>
                        <Typography variant="body2">Edit exclude from export</Typography>
                      </MenuItem>
                    )}
                    {userCanEditProperty && (
                      <MenuItem dense divider onClick={handleEditSiteVisitRequiredClick} sx={menuItemStyle(true)}>
                        <Typography variant="body2">Edit site visit required</Typography>
                      </MenuItem>
                    )}
                    {userCanEditProperty && (
                      <MenuItem dense onClick={handleAddNoteClick} sx={menuItemStyle(false)}>
                        <Typography variant="body2">Add note</Typography>
                      </MenuItem>
                    )}
                    {userCanEditProperty && (
                      <MenuItem dense divider onClick={handleMoveBlpuClick} sx={menuItemStyle(true)}>
                        <Typography variant="body2">Move BLPU seed point</Typography>
                      </MenuItem>
                    )}
                    {userCanEditProperty && (
                      <NestedMenuItem
                        dense
                        className="nestedMenuItem"
                        rightIcon={<KeyboardArrowRightIcon />}
                        label={<Typography variant="body2">Edit address</Typography>}
                        parentMenuOpen={propertyActionsOpen}
                      >
                        <MenuItem dense onClick={handleEditAddressFieldsClick} sx={menuItemStyle(false)}>
                          <Typography variant="body2">Edit address fields</Typography>
                        </MenuItem>
                        <MenuItem dense onClick={handlePlotToPostalWizardClick} sx={menuItemStyle(false)}>
                          <Typography variant="body2">Plot to postal wizard</Typography>
                        </MenuItem>
                      </NestedMenuItem>
                    )}
                    {userCanEditProperty && (
                      <NestedMenuItem
                        dense
                        divider={!settingsContext.isScottish}
                        className={settingsContext.isScottish ? "nestedMenuItem" : "nestedMenuItemDivider"}
                        rightIcon={<KeyboardArrowRightIcon />}
                        label={<Typography variant="body2">Edit cross reference</Typography>}
                        parentMenuOpen={propertyActionsOpen}
                      >
                        <MenuItem dense onClick={handleAddCrossReferenceClick} sx={menuItemStyle(false)}>
                          <Typography variant="body2">Add cross reference</Typography>
                        </MenuItem>
                        <MenuItem dense onClick={handleRemoveCrossReferenceClick} sx={menuItemStyle(false)}>
                          <Typography variant="body2">Remove cross reference</Typography>
                        </MenuItem>
                      </NestedMenuItem>
                    )}
                    <MenuItem dense onClick={zoomToProperties} sx={menuItemStyle(true)}>
                      <Typography variant="body2">Zoom to these</Typography>
                    </MenuItem>
                    <MenuItem dense onClick={handleCreateNewList} sx={menuItemStyle(true)}>
                      <Typography variant="body2">Create new list</Typography>
                    </MenuItem>
                    <MenuItem dense divider onClick={handleRemoveFromList} sx={menuItemStyle(true)}>
                      <Typography variant="body2">Remove from list</Typography>
                    </MenuItem>
                    <MenuItem dense onClick={(event) => handleExportTo(event, "XLSX")} sx={menuItemStyle(false)}>
                      <Typography variant="body2">Export to ...</Typography>
                    </MenuItem>
                    {process.env.NODE_ENV === "development" && userCanEditProperty && (
                      <MenuItem dense disabled sx={menuItemStyle(false)}>
                        <Typography variant="body2">Move street</Typography>
                      </MenuItem>
                    )}
                    {userCanEditProperty && (
                      <MenuItem dense onClick={handleMakeChildOf} sx={menuItemStyle(false)}>
                        <Typography variant="body2">Make child of...</Typography>
                      </MenuItem>
                    )}
                  </Menu>
                )}
              </>
            )}
            {numberOfTypes === 1 && variant === "mapProperty" && (
              <>
                <ADSSelectionButton
                  variant="createList"
                  selectionCount={selectionCount}
                  onClick={handleCreateNewList}
                />
                {searchContext.currentSearchData?.results?.length > 0 && (
                  <ADSSelectionButton
                    variant="existingList"
                    selectionCount={selectionCount}
                    onClick={handleAddToExistingList}
                  />
                )}
                <ADSSelectionButton
                  variant="more"
                  selectionCount={selectionCount}
                  menuControlId="property-selection-actions-menu"
                  onClick={handlePropertySelectionActionsMenuClick}
                />
                <Menu
                  id="property-selection-actions-menu"
                  elevation={2}
                  anchorReference="anchorPosition"
                  anchorPosition={getMenuAnchorPosition(anchorPropertySelectionActionsEl)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                  keepMounted
                  open={Boolean(anchorPropertySelectionActionsEl)}
                  onClose={handlePropertySelectionActionsMenuClose}
                  sx={menuStyle}
                >
                  <MenuItem dense onClick={(event) => handleExportTo(event, "XLSX")} sx={menuItemStyle(false)}>
                    <Typography variant="body2">Export to ...</Typography>
                  </MenuItem>
                </Menu>
              </>
            )}
            {numberOfTypes === 1 && variant === "classification" && userCanEditProperty && (
              <ADSSelectionButton
                variant="deleteClassification"
                selectionCount={selectionCount}
                onClick={handleDeleteClassification}
              />
            )}
            {numberOfTypes === 1 && variant === "organisation" && userCanEditProperty && (
              <ADSSelectionButton
                variant="deleteOrganisation"
                selectionCount={selectionCount}
                onClick={handleDeleteOrganisation}
              />
            )}
            {numberOfTypes === 1 && variant === "successorCrossRef" && userCanEditProperty && (
              <ADSSelectionButton
                variant="deleteSuccessorCrossRef"
                selectionCount={selectionCount}
                onClick={handleDeleteSuccessorCrossRef}
              />
            )}
            {numberOfTypes === 1 && variant === "provenance" && userCanEditProperty && (
              <ADSSelectionButton
                variant="deleteProvenance"
                selectionCount={selectionCount}
                onClick={handleDeleteProvenance}
              />
            )}
            {numberOfTypes === 1 && variant === "mapExtent" && userCanEditProperty && (
              <ADSSelectionButton variant="mergeExtent" selectionCount={selectionCount} onClick={handleMergeExtent} />
            )}
            {numberOfTypes === 1 && variant === "crossReference" && userCanEditProperty && (
              <ADSSelectionButton
                variant="deleteCrossReference"
                selectionCount={selectionCount}
                onClick={handleDeleteCrossReference}
              />
            )}
            {numberOfTypes === 1 && variant === "wizard" && userCanEditProperty && (
              <>
                <ADSSelectionButton variant="addNote" selectionCount={selectionCount} onClick={handleAddNote} />
                <ADSSelectionButton
                  variant="deleteWizard"
                  selectionCount={selectionCount}
                  onClick={handleDeleteWizard}
                />
                <ADSSelectionButton
                  variant="more"
                  selectionCount={selectionCount}
                  menuControlId="wizard-actions-menu"
                  onClick={handleWizardActionsMenuClick}
                />
                <Menu
                  id="wizard-actions-menu"
                  elevation={2}
                  anchorReference="anchorPosition"
                  anchorPosition={getMenuAnchorPosition(anchorWizardActionsEl)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                  keepMounted
                  open={Boolean(anchorWizardActionsEl)}
                  onClose={handleWizardActionsMenuClose}
                  sx={menuStyle}
                >
                  <MenuItem dense onClick={handleEditClassification} sx={menuItemStyle(false)}>
                    <Typography variant="body2">Edit classification</Typography>
                  </MenuItem>
                  <MenuItem dense onClick={handleEditExcludeFromExport} sx={menuItemStyle(false)}>
                    <Typography variant="body2">Edit exclude from export</Typography>
                  </MenuItem>
                  <MenuItem dense onClick={handleEditSiteVisit} sx={menuItemStyle(false)}>
                    <Typography variant="body2">Edit site visit required</Typography>
                  </MenuItem>
                  <MenuItem dense onClick={handleEditLevel} sx={menuItemStyle(false)}>
                    <Typography variant="body2">Edit level</Typography>
                  </MenuItem>
                  {settingsContext.isScottish && (
                    <MenuItem dense onClick={handleEditSubLocality} sx={menuItemStyle(true)}>
                      <Typography variant="body2">Edit sub-locality</Typography>
                    </MenuItem>
                  )}
                  <MenuItem dense onClick={handleEditPostTown} sx={menuItemStyle(true)}>
                    <Typography variant="body2">Edit post town</Typography>
                  </MenuItem>
                  <MenuItem dense divider onClick={handleEditPostcode} sx={menuItemStyle(false)}>
                    <Typography variant="body2">Edit postcode</Typography>
                  </MenuItem>
                  <MenuItem dense divider onClick={handleAddNote} sx={menuItemStyle(true)}>
                    <Typography variant="body2">Add note</Typography>
                  </MenuItem>
                  <MenuItem dense onClick={handleDeleteWizard} sx={menuItemStyle(false)}>
                    <Typography variant="inherit" color="error">
                      Delete
                    </Typography>
                  </MenuItem>
                </Menu>
              </>
            )}
            {numberOfTypes === 1 && variant === "moveBlpu" && userCanEditProperty && (
              <>
                <ADSSelectionButton variant="addNote" selectionCount={selectionCount} onClick={handleAddNote} />
                <ADSSelectionButton variant="rpc" selectionCount={selectionCount} onClick={handleEditRpc} />
              </>
            )}
          </Stack>
        </Stack>
      </Box>
      <AddPropertyWizardDialog
        variant={propertyWizardType.current}
        parent={propertyWizardParent.current}
        isOpen={openPropertyWizard}
        onDone={handlePropertyWizardDone}
        onClose={handlePropertyWizardClose}
      />
      <MultiEditLogicalStatusDialog
        variant={editLogicalStatusVariant}
        propertyUprns={multiEditUprns ? multiEditUprns : []}
        isOpen={openEditLogicalStatus}
        onClose={handleEditLogicalStatusClose}
      />
      <MultiEditSingleFieldDialog
        variant={editSingleFieldVariant}
        propertyUprns={multiEditUprns ? multiEditUprns : []}
        isOpen={openEditSingleField}
        onClose={handleEditSingleFieldClose}
      />
      <MultiEditAddressFieldsDialog
        propertyUprns={multiEditUprns ? multiEditUprns : []}
        isOpen={openEditAddressFields}
        onClose={handleEditAddressFieldsClose}
      />
      <MultiEditPlotToPostalWizardDialog
        propertyUprns={multiEditUprns ? multiEditUprns : []}
        isOpen={openPlotToPostalWizard}
        onClose={handlePlotToPostalWizardClose}
      />
      <MultiEditAddCrossReferenceDialog
        propertyUprns={multiEditUprns ? multiEditUprns : []}
        isOpen={openAddCrossReference}
        onClose={handleAddCrossReferenceClose}
      />
      <MultiEditRemoveCrossReferenceDialog
        propertyUprns={multiEditUprns ? multiEditUprns : []}
        isOpen={openRemoveCrossReference}
        onClose={handleRemoveCrossReferenceClose}
      />
      <MultiEditAddClassificationDialog
        propertyUprns={multiEditUprns ? multiEditUprns : []}
        isOpen={openAddClassification}
        onClose={handleAddClassificationClose}
      />
      <MoveBLPUDialog
        propertyUprns={multiEditUprns ? multiEditUprns : []}
        isOpen={openMoveBlpu}
        onClose={handleMoveBlpuClose}
      />
      <MakeChildDialog
        isOpen={openMakeChild}
        variant="multi"
        selectedUPRNs={makeChildUprn}
        onClose={handleMakeChildClose}
      />
    </>
  );
}

export default ADSSelectionControl;
